import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SafeHtml } from '@angular/platform-browser';
import { ErrorHandlingType, IsAutomationStopped, Stage } from '@vendasta/automata';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { EndAutomationRunDialogComponent } from '../../dialogs/end-automation-run/end-automation-run-dialog.component';
import { DisplayService } from '../../automation-editor/components/common/display.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import {
  ContinueOnNewStepDialogComponent,
  CurrentAutomationService,
  STANDARD_DIALOG_WIDTH,
} from '@galaxy/automata/shared';

import { ActivityRow } from '../../shared/entities';

@Component({
  selector: 'automata-activity-cell',
  template: `
    <div class="cell-content" *ngIf="displayContent$ | async as displayContent; else loading">
      <ng-container *ngIf="!endRequested">
        <div>
          <div class="title">
            <mat-icon class="error-icon" *ngIf="!!this.element.activity.error">error</mat-icon>
            <div [innerHtml]="displayContent.title"></div>
          </div>
          <div class="subtitle" *ngIf="displayContent.subtitle" [innerHtml]="displayContent.subtitle"></div>
        </div>
        <div class="activity-actions">
          <button
            mat-stroked-button
            class="wide-action-button"
            *ngIf="canContinueFromError() | async"
            (click)="continueOnNewStep()"
          >
            {{ 'AUTOMATIONS.CONTINUE_ON_NEW_STEP.CONTINUE_ON_NEW_STEP' | translate }}
          </button>
          <button mat-stroked-button *ngIf="isWaitingDelayStep() | async" (click)="endRun()">
            {{ 'AUTOMATIONS.END_RUN.END_RUN' | translate }}
          </button>
          <button
            mat-button
            *ngIf="element?.activity?.error?.message && !showMoreDetails"
            (click)="turnOnMoreDetails()"
          >
            {{ 'AUTOMATIONS.CONTINUE_ON_NEW_STEP.SHOW_MORE_DETAILS' | translate }}
          </button>
        </div>
      </ng-container>
      <div class="title" *ngIf="endRequested">
        {{ 'AUTOMATIONS.END_RUN.PROCESSING_RUN_CANCELLATION' | translate }}
      </div>
      <div class="subtitle" *ngIf="showMoreDetails">
        {{ element?.activity?.error?.message }}
      </div>
    </div>
    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
  `,
  styleUrls: ['./automata-cells.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, MatButtonModule, GalaxyPipesModule, MatIconModule],
})
export class ActivityCellComponent implements OnInit {
  @Input() element: ActivityRow;

  displayContent$: Observable<{
    title: SafeHtml;
    subtitle: SafeHtml;
  }>;
  title$: Observable<SafeHtml>;
  subtitle$: Observable<SafeHtml>;

  endRequested: boolean;
  choseStepToContinue$$ = new BehaviorSubject<boolean>(false);

  showMoreDetails = false;

  constructor(
    private readonly displayService: DisplayService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly currentAutomationService: CurrentAutomationService,
  ) {}

  ngOnInit(): void {
    this.endRequested = this.element.activity.endRunRequested && !this.element.activity.completedProcessing;
    this.title$ = this.currentAutomationService.automation$.pipe(
      switchMap((automation) => {
        return this.displayService.getActivityDisplayTitle(
          this.element.activity.namespace,
          this.element.activity,
          automation,
        );
      }),
    );

    this.subtitle$ = this.currentAutomationService.automation$.pipe(
      switchMap((automation) => {
        return this.displayService.getActivityDisplaySubtitle(
          this.element.activity.namespace,
          this.element.activity,
          automation,
          automation?.entityType,
        );
      }),
    );

    this.displayContent$ = combineLatest([this.title$, this.subtitle$]).pipe(
      take(1),
      map(([title, subtitle]) => ({
        title: title,
        subtitle: subtitle,
      })),
    );
  }

  isWaitingDelayStep(): Observable<boolean> {
    return this.currentAutomationService.automation$.pipe(
      map(
        (automation) =>
          !IsAutomationStopped(automation) &&
          !this.element.activity.completedProcessing &&
          this.element.activity.startedProcessing > automation.runningVersion &&
          this.element.activity.workflowStep.sleep > 0,
      ),
    );
  }

  endRun(): void {
    const dialog = this.dialog.open(EndAutomationRunDialogComponent, {
      width: STANDARD_DIALOG_WIDTH,
      data: {
        automationId: this.element.activity.automationId,
        mainRunId: this.element.activity.mainRunId,
        entityId: this.element.activity.entityId,
        workflowStepId: this.element.activity.workflowStep.id,
        stepExecutionUUID: this.element.activity.stepExecutionUuid,
      },
    });
    dialog
      .afterClosed()
      .pipe(
        take(1),
        tap((endRequested) => {
          this.endRequested = endRequested;
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  // canContinueFromError when the activity has an error, and it is from a run on the current automation version
  // only when the automation setting for handling errors is set to "stop on error"
  canContinueFromError(): Observable<boolean> {
    return combineLatest([this.currentAutomationService.automation$, this.choseStepToContinue$$.asObservable()]).pipe(
      map(([automation, choseNewStep]) => {
        return (
          !IsAutomationStopped(automation) &&
          !choseNewStep &&
          automation.settings.errorHandlingType === ErrorHandlingType.ERROR_HANDLING_TYPE_STOP_ON_ERROR &&
          !!this.element.activity.error &&
          this.element.activity.stage !== Stage.STAGE_WORKFLOW_CONTINUED_FROM_ERROR &&
          this.element.activity.startedProcessing > automation.runningVersion
        );
      }),
    );
  }

  turnOnMoreDetails(): void {
    this.showMoreDetails = true;
  }

  continueOnNewStep(): void {
    this.currentAutomationService.automation$
      .pipe(
        take(1),
        switchMap((automation) => {
          const nextStepOptions = (automation.workflow || [])
            .filter((s) => !!s.name)
            .map((s) => ({ name: s.name, id: s.id }));
          return this.dialog
            .open(ContinueOnNewStepDialogComponent, {
              width: STANDARD_DIALOG_WIDTH,
              data: {
                automationId: this.element.activity.automationId,
                runId: this.element.activity.runId,
                entityId: this.element.activity.entityId,
                workflowStepId: this.element.activity.workflowStep.id,
                nextStepOptions: nextStepOptions,
              },
            })
            .afterClosed();
        }),
      )
      .subscribe((chosenNewStep) => {
        if (chosenNewStep) {
          this.choseStepToContinue$$.next(true);
        }
      });
  }
}
